import React, { useState, useEffect, useCallback } from 'react';
import { useUserStore } from '../../stores';
import { Pencil } from 'lucide-react';
import './MatchMeCard.css';

export interface MatchMeAnswers {
  tripTypes: string[];
  distance: string;
  groupType: string;
}

interface MatchMeCardProps {
  onComplete: (answers: MatchMeAnswers) => void;
  onSkip: () => void;
}

const TRIP_TYPES = [
  { value: 'beach', label: 'Beach', emoji: '🏖' },
  { value: 'mountains', label: 'Mountains', emoji: '🏔' },
  { value: 'city', label: 'City', emoji: '🏙' },
  { value: 'adventure', label: 'Adventure', emoji: '🧗' },
  { value: 'road_trip', label: 'Road Trip', emoji: '🚗' },
  { value: 'ski', label: 'Ski', emoji: '🎿' },
];

const DISTANCE_OPTIONS = [
  { value: 'close', label: 'Close to home' },
  { value: 'domestic', label: 'Domestic' },
  { value: 'international', label: 'International' },
  { value: 'surprise', label: 'Surprise me' },
];

const GROUP_OPTIONS = [
  { value: 'solo', label: 'Solo' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'friends', label: 'Friends' },
];

function getStorageKey(userId: number | undefined): string {
  return userId ? `matchme_answers_${userId}` : 'matchme_answers_anon';
}

function loadAnswers(userId: number | undefined): MatchMeAnswers | null {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveAnswers(userId: number | undefined, answers: MatchMeAnswers): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(answers));
}

const EMOJI_MAP: Record<string, string> = {
  beach: '🏖', mountains: '🏔', city: '🏙',
  adventure: '🧗', road_trip: '🚗', ski: '🎿',
};

const MatchMeCard: React.FC<MatchMeCardProps> = ({ onComplete, onSkip }) => {
  const user = useUserStore((s) => s.user);
  const [saved, setSaved] = useState<MatchMeAnswers | null>(null);
  const [editing, setEditing] = useState(false);
  const [tripTypes, setTripTypes] = useState<string[]>([]);
  const [distance, setDistance] = useState('');
  const [groupType, setGroupType] = useState('');

  useEffect(() => {
    const existing = loadAnswers(user?.id);
    if (existing) setSaved(existing);
  }, [user?.id]);

  const handleTripTypeToggle = useCallback((value: string) => {
    setTripTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  }, []);

  const handleSubmit = () => {
    const answers: MatchMeAnswers = { tripTypes, distance, groupType };
    saveAnswers(user?.id, answers);
    setSaved(answers);
    setEditing(false);
    onComplete(answers);
  };

  const handleEdit = () => {
    if (saved) {
      setTripTypes(saved.tripTypes);
      setDistance(saved.distance);
      setGroupType(saved.groupType);
    }
    setEditing(true);
  };

  const handleSkip = () => {
    setSaved({ tripTypes: [], distance: '', groupType: '' });
    onSkip();
  };

  const canSubmit = tripTypes.length > 0 || !!distance || !!groupType;

  // Compact summary row when completed
  if (saved && !editing) {
    const labels = saved.tripTypes.map(t => EMOJI_MAP[t] || t).join(' ');
    const distLabel = DISTANCE_OPTIONS.find(d => d.value === saved.distance)?.label || '';
    const groupLabel = GROUP_OPTIONS.find(g => g.value === saved.groupType)?.label || '';
    const parts = [labels, distLabel, groupLabel].filter(Boolean);

    if (parts.length === 0) return null;

    return (
      <div className="matchme-summary">
        <span className="matchme-summary-text">{parts.join(' · ')}</span>
        <button className="matchme-summary-edit" onClick={handleEdit}>
          <Pencil size={12} /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="matchme-card">
      <div className="matchme-header">
        <span className="matchme-eyebrow">Match me with plans for</span>
        <button className="matchme-skip" onClick={handleSkip}>Skip</button>
      </div>

      <div className="matchme-row">
        <span className="matchme-row-label">Vibe</span>
        <div className="matchme-chips">
          {TRIP_TYPES.map(t => (
            <button
              key={t.value}
              className={`matchme-chip ${tripTypes.includes(t.value) ? 'active' : ''}`}
              onClick={() => handleTripTypeToggle(t.value)}
            >
              <span className="matchme-chip-emoji">{t.emoji}</span> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="matchme-row">
        <span className="matchme-row-label">Distance</span>
        <div className="matchme-chips">
          {DISTANCE_OPTIONS.map(d => (
            <button
              key={d.value}
              className={`matchme-chip ${distance === d.value ? 'active' : ''}`}
              onClick={() => setDistance(distance === d.value ? '' : d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="matchme-row">
        <span className="matchme-row-label">With</span>
        <div className="matchme-chips">
          {GROUP_OPTIONS.map(g => (
            <button
              key={g.value}
              className={`matchme-chip ${groupType === g.value ? 'active' : ''}`}
              onClick={() => setGroupType(groupType === g.value ? '' : g.value)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="matchme-actions">
        <button className="matchme-next" onClick={handleSubmit} disabled={!canSubmit}>
          Show me plans
        </button>
      </div>
    </div>
  );
};

export default MatchMeCard;
